import { Flex, Text } from '@chakra-ui/react';
import { ColumnDef, createColumnHelper, HeaderContext } from '@tanstack/react-table';

import { CustomAssetLifecycle, CustomAssetLifecycleColumn } from '@/cip/_openapi/portfolio-optimizer';
import { booleanCol, moneyCol, numberCol, percentageCol, textCol } from '@/cip/widgets/Table/columnHelpers/colDef';
import { MoneyCell } from '@/cip/widgets/Table/MoneyCell';
import { NumberCell } from '@/cip/widgets/Table/NumberCell';
import { TextCell } from '@/cip/widgets/Table/TextCell';

import { ExposureBar } from './ExposureBar';

export type LifecycleRow = CustomAssetLifecycle['rows'][number];

// Keyed off `LifecycleColumnRole`'s values (data-pipelines' lifecycle_report.py) - the API only
// promises `role: string`, so an unrecognized value just gets no accent color rather than erroring.
export const ROLE_ACCENT_COLORS: Record<string, string> = {
    identity: 'teal.900',
    fact: 'gray.700',
    stage: 'orange.900',
    check: 'blue.900',
    delta: 'green.600',
    detail: 'green.900',
};

const columnHelper = createColumnHelper<LifecycleRow>();

const buildLeafColDef = (column: CustomAssetLifecycleColumn) => {
    switch (column.columnType) {
        case 'money':
            return moneyCol<LifecycleRow>({ id: column.name });
        case 'percentage':
            return percentageCol<LifecycleRow>({ id: column.name });
        case 'number':
            return numberCol<LifecycleRow>({ id: column.name });
        case 'boolean':
            return booleanCol<LifecycleRow>({ id: column.name });
        default:
            return textCol<LifecycleRow>({ id: column.name });
    }
};

// The expand/collapse control for these columns lives in the outline row PositionLifeCycleGrid
// renders above the header, not here - keeps it visually distinct from the sort/filter icons.
const GroupHeader = (label: string, childCount: number): (() => JSX.Element) =>
    function GroupHeaderCell() {
        return (
            <Flex alignItems="center" gap={1}>
                <Text>{label}</Text>
                <Text fontSize="xs" opacity={0.7}>
                    ({childCount})
                </Text>
            </Flex>
        );
    };

const buildFooter = (
    column: CustomAssetLifecycleColumn,
    isFirstColumn: boolean,
): ((context: HeaderContext<LifecycleRow, unknown>) => JSX.Element | string) | undefined => {
    if (isFirstColumn) {
        return ({ table }) => `TOTAL (${table.getFilteredRowModel().rows.length})`;
    }

    if (column.columnType !== 'money' && column.columnType !== 'number' && column.columnType !== 'percentage') {
        return undefined;
    }

    return function FooterSumCell({ table }) {
        const sum = table.getFilteredRowModel().rows.reduce((total, row) => {
            const value = row.getValue(column.name);
            return total + (typeof value === 'number' ? value : 0);
        }, 0);

        if (column.columnType === 'money') {
            return <MoneyCell value={sum} />;
        }
        if (column.columnType === 'percentage') {
            return <TextCell value={`${(sum * 100).toFixed(1)}%`} textAlign="right" />;
        }
        return <NumberCell value={sum} />;
    };
};

const buildExposureBarColumn = (rows: LifecycleRow[]) => {
    const barMax = Math.max(0, ...rows.map((row) => Math.max(row.currentExposure ?? 0, row.finalTargetExposure ?? 0)));

    return columnHelper.accessor((row) => Math.abs((row.currentExposure ?? 0) - (row.finalTargetExposure ?? 0)), {
        id: 'exposureVsTarget',
        header: 'Exposure vs Target',
        cell: ({ row }) => <ExposureBar row={row.original} max={barMax} />,
        enableColumnFilter: false,
    });
};

export const buildPositionLifeCycleColumns = (columnDefs: CustomAssetLifecycleColumn[], rows: LifecycleRow[]) => {
    const childrenByParent = new Map<string, CustomAssetLifecycleColumn[]>();
    columnDefs.forEach((column) => {
        if (column.role === 'detail' && column.parentId) {
            const siblings = childrenByParent.get(column.parentId) ?? [];
            siblings.push(column);
            childrenByParent.set(column.parentId, siblings);
        }
    });

    const defaultColumnVisibility: Record<string, boolean> = {};
    columnDefs
        .filter((column) => column.role === 'detail')
        .forEach((column) => {
            defaultColumnVisibility[column.name] = false;
        });

    const accentColorByColumnId: Record<string, string> = {};
    const detailChildIdsByColumnId: Record<string, string[]> = {};

    const columns: ColumnDef<LifecycleRow, any>[] = columnDefs.map((column, index) => {
        const base = buildLeafColDef(column);
        const children = childrenByParent.get(column.name);
        accentColorByColumnId[column.name] = ROLE_ACCENT_COLORS[column.role];

        const childIds = children?.map((child) => child.name);
        if (childIds?.length) {
            detailChildIdsByColumnId[column.name] = childIds;
        }

        return columnHelper.accessor(column.name, {
            ...base,
            header: childIds?.length ? GroupHeader(base.header, childIds.length) : base.header,
            footer: buildFooter(column, index === 0),
            meta: {
                ...base.meta,
                isSticky: index === 0,
            },
        });
    });

    // The exposure bar is a frontend-derived column - slot it in right after the identity block.
    const firstNonIdentityIndex = columnDefs.findIndex((column) => column.role !== 'identity');
    const insertAt = firstNonIdentityIndex === -1 ? columns.length : firstNonIdentityIndex;
    columns.splice(insertAt, 0, buildExposureBarColumn(rows));
    accentColorByColumnId['exposureVsTarget'] = ROLE_ACCENT_COLORS.check;

    return { columns, defaultColumnVisibility, accentColorByColumnId, detailChildIdsByColumnId };
};
